import { DragEvent, useEffect, useState } from 'react'
import { getStoredApiKey } from '../../utils/apiKeyStorage'
import { buildInteriorPrompt } from '../../utils/interiorPrompt'
import { fileToBase64, transformImageWithGemini, GeminiApiError } from '../../utils/geminiImage'
import { roomOptions, styleOptions, colorOptions, demoResults } from '../../data/interiorOptions'

export interface TransformOutcome {
  src: string
  isDemo: boolean
}

const MAX_FILE_BYTES = 10 * 1024 * 1024

function firstValue(value: string | string[]): string {
  return Array.isArray(value) ? value[0] ?? '' : value
}

function labelFor(options: { value: string; label: string }[], value: string): string {
  return options.find((o) => o.value === value)?.label || value
}

export function usePhotoTransformer() {
  const [apiKey, setApiKey] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [roomType, setRoomType] = useState<string | string[]>('obyvacka')
  const [style, setStyle] = useState<string | string[]>('minimalisticky')
  const [colorId, setColorId] = useState('neutral')
  const [budget, setBudget] = useState<number | [number, number]>(3000)
  const [isDragging, setIsDragging] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<TransformOutcome | null>(null)

  useEffect(() => {
    setApiKey(getStoredApiKey())
  }, [])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const budgetValue = Array.isArray(budget) ? budget[1] : budget

  const handleFile = (selected: File | null) => {
    if (!selected) return
    if (!selected.type.startsWith('image/')) {
      setError('Nahraj prosím obrázok vo formáte JPG alebo PNG.')
      return
    }
    if (selected.size > MAX_FILE_BYTES) {
      setError('Fotka je väčšia ako 10 MB. Zmenši ju alebo vyber inú.')
      return
    }
    setError(null)
    setResult(null)
    setFile(selected)
    setPreviewUrl(URL.createObjectURL(selected))
  }

  const handleDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    setIsDragging(false)
    handleFile(e.dataTransfer.files?.[0] || null)
  }

  const reset = () => {
    setFile(null)
    setPreviewUrl(null)
    setResult(null)
    setError(null)
  }

  const generate = async () => {
    if (!file || !previewUrl) {
      setError('Najprv nahraj fotografiu svojho priestoru.')
      return
    }
    setError(null)
    setIsLoading(true)

    const room = firstValue(roomType)
    const roomLabel = labelFor(roomOptions, room)
    const styleLabel = labelFor(styleOptions, firstValue(style))
    const colorLabel = colorOptions.find((c) => c.id === colorId)?.label || 'Neutrálna'
    const budgetLabel = `€${budgetValue.toLocaleString('sk-SK')}`

    try {
      if (apiKey) {
        const { base64, mimeType } = await fileToBase64(file)
        const prompt = buildInteriorPrompt({ roomLabel, styleLabel, colorLabel, budgetLabel })
        const transformed = await transformImageWithGemini({ apiKey, imageBase64: base64, mimeType, prompt })
        setResult({ src: `data:${transformed.mimeType};base64,${transformed.imageBase64}`, isDemo: false })
      } else {
        await new Promise((resolve) => setTimeout(resolve, 2000))
        setResult({ src: demoResults[room] || demoResults.obyvacka, isDemo: true })
      }
    } catch (err) {
      setError(
        err instanceof GeminiApiError
          ? err.message
          : 'Pri generovaní návrhu sa niečo pokazilo. Skús to prosím znova.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  const download = () => {
    if (!result) return
    const link = document.createElement('a')
    link.href = result.src
    link.download = 'interior-ai-navrh.png'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return {
    apiKey,
    setApiKey,
    previewUrl,
    roomType,
    setRoomType,
    style,
    setStyle,
    colorId,
    setColorId,
    budget,
    setBudget,
    budgetValue,
    isDragging,
    setIsDragging,
    isLoading,
    error,
    result,
    handleFile,
    handleDrop,
    reset,
    generate,
    download,
  }
}
